import { FilledInput, makeStyles, MenuItem, Select, Tooltip, withStyles } from '@material-ui/core';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { Linked, useLinked } from '@type-r/react';
import * as React from 'react';
import { useContext } from 'react';
import { colors } from '../common';
import { Input, InputProps, LxInputProps, ReadOnly } from './inputs';

export const Option = MenuItem;

const ErrorTooltip = withStyles({
    tooltip : {
        color: 'white',
        backgroundColor: '#FF274B',

        padding: 10,
        fontFamily: 'Montserrat',
        fontSize: 12,
        fontWeight: 600,
    },

    arrow : {
        color: '#FF274B',
    },
})(
    ({ ...props } : any) =>
        <Tooltip
            placement="top-end"
            arrow={ true }
            { ...props }
        />
);

const BaseSelectInput = withStyles({
    root : {},
    input : {},

    inputRoot : {
        flexGrow: 1
    },

    errorIcon : {
        margin: 'auto 0'
    }
})(
    ({ $value, classes, error, ...props} : {
        $value? : Linked<any>,
        classes? : any,
        error? : string
    }) => {
        return (
            <div className={ classes.root }>
                { $value ?
                        <FilledInput
                            classes={{
                                root: classes.inputRoot,
                                input: classes.input
                            }}
                            value={ $value.value == null ? '' : $value.value }
                            onChange={ e => $value.set( e.target.value ) }
                            { ...props }
                            disableUnderline={ true }
                        />
                    :
                        <FilledInput
                            classes={{
                                root: classes.inputRoot,
                                input: classes.input
                            }}
                            { ...props }
                            disableUnderline={ true }
                        />
                }
                { error ?
                    <ErrorTooltip title={ error } className={ classes.errorIcon }>
                        <ErrorOutlineIcon />
                    </ErrorTooltip>
                : null }
            </div>
        );
    }
);

export interface BaseSelectProps extends LxInputProps<any> {
    disabled? : boolean
    label? : string | JSX.Element
    classes? : any
    children : (JSX.Element | JSX.Element[])[]
}

export const BaseSelect = withStyles({
    column : {
        flexDirection: "column",
    },

    row : {
        flexDirection: 'row',
    },

    root : {
        marginTop: 4,
        marginBottom: 4,
        display: 'flex',
        flex: 2,

        '&:focus-within' : {
            color: '#1084D3',

            '& .label' : {
                color: '#1084D3',
            }
        },

        '&.error' : {
            color: '#FF274B !important',

            '& .label' : {
                color: '#FF274B !important',
            }
        },

        '& .MuiInputBase-root' : {
            backgroundColor: ( props : any ) => props.disabled ? '#F9F9F9' : 'white',
            '&:hover' : {
            },
        }
    },

    label : {},

    selectRoot : {
        '&:focus': {
            backgroundColor: '#F6FCFF',
            borderColor: '#1084D3'
        },
    },

    select : {
    },

    selectInputRoot : {
        borderRadius: 0,
        display: 'flex',
        flexGrow: 1
    },

    selectInput : {
        padding: 7,
    }
})(
    ({ label, children, $value, classes, ...props } : BaseSelectProps ) => {
        const ro = useContext( ReadOnly ) || props.readOnly || !$value || props.disabled,
            error = (!ro && $value.error) || '',
            maybeError = error ? ' error' : '';

        return (
            label ?
                <label className={ classes.root + ' ' + classes.row + maybeError }>
                    <span className={ classes.label + ' label' }>
                        { label }
                        { (props.required === undefined && error === 'Required') || ( props.required && !ro ) ?
                        <span className={ classes.required }>*</span>
                    : null }
                    </span>
                    <Select
                        classes={{
                            root : classes.selectRoot + ' select-root',
                            select : classes.select
                        }}
                        readOnly={ ro }
                        input={
                            <BaseSelectInput
                                classes={{
                                    root: classes.selectInputRoot + ' select-input-root',
                                    input: classes.selectInput
                                }}
                                $value={ $value }
                                error={ error }
                            />
                        }
                        native={ false }
                        { ...props }
                    >
                        { children }
                    </Select>
                </label>
            :
                <div className={ classes.root + ' ' + classes.row + maybeError }>
                    <Select
                        classes={{
                            root : classes.selectRoot + ' select-root',
                            select : classes.select
                        }}
                        readOnly={ ro }
                        input={
                            <BaseSelectInput
                                classes={{
                                    root: classes.selectInputRoot + ' select-input-root',
                                    input: classes.selectInput
                                }}
                                $value={ $value }
                                error={ error }
                            />
                        }
                        native={ false }
                        { ...props }
                    >
                        { children }
                    </Select>
                </div>
        )
    }
)

export const PickItem = ({ items, filter, onChange, $value, format, maxLength = 10, preserveFilter, children, bottom, $showDropdown, showMore, label, insidePopup, ...rest } : {
    $value? : Linked<object>
    format? : ( x : object ) => string
    items 
    maxLength? : number
    preserveFilter? : boolean
    filter( x : any, filter : string ) : boolean
    onChange?( value : any ) : void
    children : ( x : any, idx : number ) => React.ReactNode,
    bottom? : React.ReactNode,
    $showDropdown? : Linked<boolean>,
    showMore? : boolean,
    label?: string,
    insidePopup?: boolean,
} & Omit<InputProps, '$value'> ) => {
    const classes = usePickItemStyles({insidePopup}),
    ro = useContext( ReadOnly ) || rest.readOnly || !$value || rest.disabled,
    error = ro ? '' : $value.error;

    const $show = $showDropdown || useLinked( false );

    const $length = useLinked(maxLength);

    const $filter = useLinked( '' ),
        { value } = $filter;

    const filtered = items.filter( x => !value || filter( x, value ) );

    const $options = useLinked([]);

    React.useEffect(() => {
        const options = [];
        for( let i = 0; i < $length.value && i < filtered.length; i++ ){
            const el = filtered[ i ];
            options.push(
                <li key={el.cid || i}
                    onMouseDown={ () => {
                        $value && $value.set( el );
                        onChange && onChange( el );
                    }}
                >
                    { children( el , i ) }
                </li>
            )
        }
    
        if( !filtered.length ){
            options.push(
                <div>
                    No results, refine the filter
                </div>
            )
        }

        if( filtered.length > $length.value ){
            options.push(
                <div onMouseDown={(e) => { 
                    if (showMore) {
                        e.stopPropagation(); 
                        e.preventDefault(); 
                        addMoreOptions();
                    }
                }}>
                    { showMore ? `Show more (${filtered.length - $length.value})` : `${filtered.length - $length.value} more` } 
                </div>
            )
        }
        else if (bottom) {
            options.push(bottom);
        }

        $options.set(options);
    
    }, [$show.value, $filter.value, $length.value])

    const addMoreOptions = () => {
        $length.set($length.value + maxLength);
    };

    return (
        label ?
            <label className={ classes.root }>
                <span className={ classes.label }>
                    {label}
                    { (rest.required === undefined && error === 'Required') || ( rest.required && !ro && !$value.value ) ?
                        <span className={ classes.required }>*</span>
                    : null }
                </span>
                <div className={ classes.pickItemRoot }
                    onFocus={ ro ? $show.false : $show.true }
                    onBlur={ () => {
                        preserveFilter || $filter.set( '' );
                        $show.false();
                    }}
                >           
                    { $show.isTruthy ?
                    <>
                        <Input $value={ $filter } readOnly={ro} {...rest}
                        />

                        <ul>
                            { $options.value }
                        </ul>
                    </>
                    : 
                        <Input readOnly={ro} $value={ Linked.value( format ? format($value.value) : $filter.value, x => undefined ) } {...rest} />
                    }
                </div>
            </label>
        : <div className={ classes.pickItemRoot }
            onFocus={ $show.true }
            onBlur={ () => {
                preserveFilter || $filter.set( '' );
                $show.false();
            }}
        >           

            { $show.isTruthy ?
            <>
                <Input $value={ $filter } {...rest}
                />

                <ul>
                    { $options.value }
                </ul>
            </>
            : 
                <Input $value={ Linked.value( format ? format($value.value) : $filter.value, x => undefined ) } {...rest} />
            }
        </div>
    )
}

const usePickItemStyles = makeStyles({
    root : {
        display: 'flex',
        flexDirection: 'row',
        color: '#767D84',
        fontFamily: 'Montserrat',
        fontSize: 12,
        fontWeight: 500,
        marginBottom: 8,
        '&:focus-within' : {
            color: '#1084D3',
            fontWeight: 600,
        }
    },
    label: {
        flex: 2,
        alignSelf: 'center',
    },
    pickItemRoot : {
        flex: 3,
        '& ul' : {
            listStyleType : 'none',
            margin: '4px 0 0 0',
            padding: 0,
            position: (props: any) => props.insidePopup ? 'fixed' : 'absolute',
            backgroundColor: 'white',
            boxShadow: '1px 1px 2px 0px #808080',
            'z-index': 10,
            maxHeight: '450px',
            overflow: 'auto',

            '& li' : {
                cursor: "pointer",
                padding: '0.5em',
                '&:hover' : {
                    backgroundColor: 'whitesmoke'
                }
            }
        }
    },
    required : {
        color: colors.red,
        verticalAlign: 'middle',
        fontSize: 16,
        paddingLeft: 4,
    }
});